import {
  AutomationStep,
  AutomationStepWithChildren,
  AutomationStepWithDirectChildren,
  ControlBlockingMode,
  DispatchStep,
  EntityModeType,
  EntityDataInputConfig,
  TriggerStep,
  TriggerType,
} from '@/types/automationSteps';
import {
  ConditionAction,
  DataInput,
  DynamicDataInputConfig,
  DataInputType,
  FileSelectionMode,
  FilterMode,
  InputSource,
  StepType,
} from '@/types/automationFields.ts';

import { IntegrationProvider } from '@/types/integrations.ts';
import {
  isStandaloneCapabilityProvider,
  providerUsesIntegrationInstance,
} from '@/utils/providerKinds.ts';

const isPresentedLocalSubflowCall = (
  step: AutomationStep,
): step is AutomationStepWithDirectChildren =>
  step.step_type === StepType.LocalSubflowCall &&
  'children' in step &&
  Array.isArray(step.children);

// Type guard for steps that can contain children
export const isStepWithChildren = (step: AutomationStep): step is AutomationStepWithChildren =>
  [
    StepType.Iterate,
    StepType.Repeat,
    StepType.Retry,
    StepType.FailureHandler,
    StepType.Conditional,
  ].includes(step.step_type) || isPresentedLocalSubflowCall(step);

export const isStepWithDirectChildren = (
  step: AutomationStep,
): step is AutomationStepWithDirectChildren =>
  [StepType.Iterate, StepType.Repeat, StepType.Retry].includes(step.step_type) ||
  isPresentedLocalSubflowCall(step);

export const getAllStepChildren = (step: AutomationStepWithChildren): AutomationStep[] => {
  if (step.step_type === StepType.Conditional) {
    return step.conditions.flatMap((c) => c.children);
  } else if (step.step_type === StepType.FailureHandler) {
    return [...step.children, ...step.handler_children];
  } else {
    return step.children;
  }
};
// Depth-first search for a step by id
export function findStepById(
  steps: AutomationStep[] | undefined,
  targetId: string,
): AutomationStep | undefined {
  if (!steps?.length) return undefined;

  for (const s of steps) {
    if (s.step_id === targetId) return s;
    if (isStepWithChildren(s)) {
      const found = findStepById(getAllStepChildren(s), targetId);
      if (found) return found;
    }
  }
  return undefined;
}

export function collectTriggerAndDispatchSteps(
  steps: AutomationStep[],
): Array<TriggerStep | DispatchStep> {
  if (!steps?.length) return [];

  const out: Array<TriggerStep | DispatchStep> = [];

  for (const s of steps) {
    if (s.step_type === StepType.Trigger || s.step_type === StepType.Dispatch) {
      out.push(s as TriggerStep | DispatchStep);
    }
    if (isStepWithChildren(s)) {
      const allChildren = getAllStepChildren(s);
      out.push(...collectTriggerAndDispatchSteps(allChildren));
    }
  }
  return out;
}

export function indexStepsById(steps?: AutomationStep[]): Record<string, AutomationStep> {
  const byId: Record<string, AutomationStep> = {};
  if (!steps?.length) return byId;

  const stack: AutomationStep[] = [...steps]; // iterative DFS
  while (stack.length) {
    const s = stack.pop()!;
    if (!(s.step_id in byId)) {
      byId[s.step_id] = s;
    }
    if (isStepWithChildren(s)) {
      const allChildren = getAllStepChildren(s);
      for (let i = 0; i < allChildren.length; i++) {
        stack.push(allChildren[i]);
      }
    }
  }

  return byId;
}

export function collectUsedIntegrations(
  steps?: AutomationStep[],
  providers?: IntegrationProvider[],
): IntegrationProvider[] {
  if (!steps || !providers) return [];

  const relevantSteps = collectTriggerAndDispatchSteps(steps);

  const filteredProviders = providers.filter((p) => !isStandaloneCapabilityProvider(p));

  return relevantSteps
    .map((step: TriggerStep | DispatchStep) => {
      if (step.step_type === StepType.Trigger && step.trigger_type === TriggerType.Manual) return;
      return filteredProviders.find((p) =>
        providerUsesIntegrationInstance(p)
          ? p.instances.find((i) => i.id === step.integration_config.integration_id)
          : p.name === step.integration_config.integration_id,
      );
    })
    .filter((p) => !!p);
}

export function getActiveConditionId(
  step: AutomationStep | undefined,
  activeConditionIds: Record<string, string>,
): string | undefined {
  let conditionId = undefined;
  if (step?.step_type === StepType.Conditional) {
    const { step_id, conditions } = step;
    conditionId =
      activeConditionIds[step_id] ??
      conditions.filter((c) => c.action === ConditionAction.ChildSteps)[0]?.condition_id;
  }
  if (step?.step_type === StepType.FailureHandler) {
    conditionId = activeConditionIds[step.step_id] ?? 'protected';
  }
  return conditionId;
}

export type StepDisabledInfo = {
  isExplicitlyDisabled: boolean;
  isEffectivelyDisabled: boolean;
  isInheritedDisabled: boolean;
};

const ENABLED_DISABLED_INFO: StepDisabledInfo = {
  isExplicitlyDisabled: false,
  isEffectivelyDisabled: false,
  isInheritedDisabled: false,
};

function acceptsDataInput(source: InputSource | undefined): boolean {
  return source === InputSource.FromDataset || source === InputSource.Both;
}

function getDynamicSourceStepId(dataInput: DataInput | undefined): string | undefined {
  if (!dataInput || dataInput.data_input_type !== DataInputType.Dynamic) return undefined;
  return (dataInput.config as DynamicDataInputConfig).source_step_id ?? undefined;
}

function getDynamicSourceStepIds(dataInputs: DataInput[] | undefined): (string | undefined)[] {
  return dataInputs?.map(getDynamicSourceStepId) ?? [];
}

function collectDynamicSourceStepIdsFromValue(value: unknown): string[] {
  if (!value || typeof value !== 'object') return [];

  const maybeDataInput = value as Partial<DataInput>;
  if (maybeDataInput.data_input_type === DataInputType.Dynamic) {
    const sourceStepId = getDynamicSourceStepId(maybeDataInput as DataInput);
    return sourceStepId ? [sourceStepId] : [];
  }

  if (Array.isArray(value)) {
    return value.flatMap((item) => collectDynamicSourceStepIdsFromValue(item));
  }

  return Object.values(value).flatMap((item) => collectDynamicSourceStepIdsFromValue(item));
}

function collectStepDependencyIds(step: AutomationStep): string[] {
  const dependencyIds: Array<string | undefined> = [...(step.wait_for_step_ids ?? [])];

  switch (step.step_type) {
    case StepType.Trigger:
    case StepType.Dispatch:
      dependencyIds.push(
        ...collectDynamicSourceStepIdsFromValue(step.integration_config.config_values),
      );
      break;
    case StepType.ExtractData:
      dependencyIds.push(...step.data_inputs.map(getDynamicSourceStepId));
      if (acceptsDataInput(step.instructions_source)) {
        dependencyIds.push(...getDynamicSourceStepIds(step.instructions_data_inputs));
      }
      if (acceptsDataInput(step.extract_fields_source)) {
        dependencyIds.push(getDynamicSourceStepId(step.extract_fields_data_input));
      }
      if (step.extract_entities && step.entity_mode === EntityModeType.DataInput) {
        dependencyIds.push(
          getDynamicSourceStepId((step.entity_config as EntityDataInputConfig).data_input),
        );
      }
      break;
    case StepType.TransformData:
      dependencyIds.push(...step.data_inputs.map(getDynamicSourceStepId));
      if (acceptsDataInput(step.transformation_logic_source)) {
        dependencyIds.push(...getDynamicSourceStepIds(step.transformation_logic_data_inputs));
      }
      break;
    case StepType.SubAutomation:
      dependencyIds.push(
        ...step.trigger_input_mappings.map((mapping) => getDynamicSourceStepId(mapping.data_input)),
      );
      break;
    case StepType.LocalSubflowCall:
      dependencyIds.push(
        ...step.input_bindings.map((binding) => getDynamicSourceStepId(binding.data_input)),
      );
      break;
    case StepType.Iterate:
      dependencyIds.push(getDynamicSourceStepId(step.data_input));
      break;
    case StepType.Repeat:
      dependencyIds.push(
        ...step.repeat_data_inputs.map((input) => getDynamicSourceStepId(input.data_input)),
      );
      dependencyIds.push(...step.condition_data_inputs.map(getDynamicSourceStepId));
      if (acceptsDataInput(step.condition_logic_source)) {
        dependencyIds.push(...getDynamicSourceStepIds(step.condition_logic_data_inputs));
      }
      break;
    case StepType.Retry:
      break;
    case StepType.FailureHandler:
      dependencyIds.push(
        ...(step.protected_input_bindings || []).map((binding) =>
          getDynamicSourceStepId(binding.data_input),
        ),
        ...(step.handler_input_bindings || []).map((binding) =>
          getDynamicSourceStepId(binding.data_input),
        ),
      );
      break;
    case StepType.Conditional:
      step.conditions.forEach((condition) => {
        dependencyIds.push(...condition.condition_data_inputs.map(getDynamicSourceStepId));
        if (acceptsDataInput(condition.condition_logic_source)) {
          dependencyIds.push(...getDynamicSourceStepIds(condition.condition_logic_data_inputs));
        }
        if (condition.action === ConditionAction.ReturnDataset) {
          dependencyIds.push(
            ...condition.data_outputs.map((output) => getDynamicSourceStepId(output.data_input)),
          );
        }
      });
      break;
    case StepType.CreateDirectory:
      dependencyIds.push(...step.data_inputs.map(getDynamicSourceStepId));
      break;
    case StepType.GetFileFromDirectory:
      dependencyIds.push(getDynamicSourceStepId(step.data_input));
      if (
        (step.selection_mode ?? FileSelectionMode.NaturalLanguage) ===
          FileSelectionMode.NaturalLanguage &&
        acceptsDataInput(step.file_to_get_source)
      ) {
        dependencyIds.push(getDynamicSourceStepId(step.file_to_get_data_input));
      }
      if (
        (step.selection_mode ?? FileSelectionMode.NaturalLanguage) !==
          FileSelectionMode.NaturalLanguage &&
        acceptsDataInput(step.selection_value_source)
      ) {
        dependencyIds.push(getDynamicSourceStepId(step.selection_value_data_input));
      }
      break;
    case StepType.Filter:
      dependencyIds.push(getDynamicSourceStepId(step.data_input));
      if (
        step.filter_mode === FilterMode.Condition &&
        acceptsDataInput(step.filter_condition_source)
      ) {
        dependencyIds.push(...getDynamicSourceStepIds(step.filter_condition_data_inputs));
      }
      if (step.filter_mode === FilterMode.Selection && acceptsDataInput(step.selection_source)) {
        dependencyIds.push(...getDynamicSourceStepIds(step.selection_data_inputs));
      }
      break;
    case StepType.Validate:
      dependencyIds.push(...step.data_inputs.map(getDynamicSourceStepId));
      if (acceptsDataInput(step.validation_logic_source)) {
        dependencyIds.push(...getDynamicSourceStepIds(step.validation_logic_data_inputs));
      }
      break;
    case StepType.HumanDecisionGate:
      dependencyIds.push(...step.data_inputs.map(getDynamicSourceStepId));
      break;
  }

  return dependencyIds.filter((id): id is string => !!id);
}

function collectChildSteps(step: AutomationStep): AutomationStep[] {
  if (!isStepWithChildren(step)) return [];
  return getAllStepChildren(step);
}

function addChildrenToQueue(step: AutomationStep, queue: string[]) {
  for (const child of collectChildSteps(step)) {
    queue.push(child.step_id);
  }
}

export function buildStepDisabledInfoById(steps: AutomationStep[]): Map<string, StepDisabledInfo> {
  const stepsById = new Map<string, AutomationStep>();
  const dependentsByStepId = new Map<string, Set<string>>();
  const explicitDisabledStepIds = new Set<string>();

  function addDependent(dependencyStepId: string, dependentStepId: string) {
    const dependents = dependentsByStepId.get(dependencyStepId) ?? new Set<string>();
    dependents.add(dependentStepId);
    dependentsByStepId.set(dependencyStepId, dependents);
  }

  function visitScope(scopeSteps: AutomationStep[]) {
    scopeSteps.forEach((step) => {
      stepsById.set(step.step_id, step);
      if (step.is_disabled && step.step_type !== StepType.Trigger) {
        explicitDisabledStepIds.add(step.step_id);
      }

      collectStepDependencyIds(step).forEach((dependencyStepId) =>
        addDependent(dependencyStepId, step.step_id),
      );

      if (step.step_type === StepType.Validate) {
        const laterStepIds = scopeSteps
          .slice(scopeSteps.indexOf(step) + 1)
          .map((laterStep) => laterStep.step_id);
        const blockedStepIds =
          step.blocking_mode === ControlBlockingMode.None
            ? []
            : step.blocking_mode === ControlBlockingMode.AllLaterStepsInScope
              ? laterStepIds
              : step.blocking_step_ids;
        blockedStepIds.forEach((blockedStepId) => addDependent(step.step_id, blockedStepId));
      }
    });

    scopeSteps.forEach((step) => {
      if (isStepWithChildren(step)) {
        if (step.step_type === StepType.Conditional) {
          step.conditions.forEach((condition) => visitScope(condition.children));
        } else if (step.step_type === StepType.FailureHandler) {
          visitScope(step.children);
          visitScope(step.handler_children);
        } else {
          visitScope(step.children);
        }
      }
    });
  }

  visitScope(steps);

  const effectiveDisabledStepIds = new Set<string>();
  const queue = [...explicitDisabledStepIds];

  while (queue.length > 0) {
    const stepId = queue.shift()!;
    if (effectiveDisabledStepIds.has(stepId)) continue;

    effectiveDisabledStepIds.add(stepId);
    const step = stepsById.get(stepId);
    if (step) {
      addChildrenToQueue(step, queue);
    }

    dependentsByStepId.get(stepId)?.forEach((dependentStepId) => {
      queue.push(dependentStepId);
    });
  }

  const disabledInfoById = new Map<string, StepDisabledInfo>();
  stepsById.forEach((_step, stepId) => {
    const isExplicitlyDisabled = explicitDisabledStepIds.has(stepId);
    const isEffectivelyDisabled = effectiveDisabledStepIds.has(stepId);
    disabledInfoById.set(stepId, {
      isExplicitlyDisabled,
      isEffectivelyDisabled,
      isInheritedDisabled: isEffectivelyDisabled && !isExplicitlyDisabled,
    });
  });

  return disabledInfoById;
}

export function getStepDisabledInfo(
  steps: AutomationStep[] | undefined,
  stepId: string,
): StepDisabledInfo {
  if (!steps) return ENABLED_DISABLED_INFO;
  return buildStepDisabledInfoById(steps).get(stepId) ?? ENABLED_DISABLED_INFO;
}
